import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import { useAuth } from '@structural-climbing/auth'
import { Button, Checkbox, Spinner } from '@structural-climbing/ui'
import type { PeriodParams } from './PeriodSelector'
import { GymDialog } from '../../components/GymDialog'

interface Props {
  onClose: () => void
  periodParams: PeriodParams
  currency: string
  periodLabel: string
}

interface OrderRow {
  id: string
  created_at: string
  payment_method: string
  total_amount: number
  plan_name: string
  member_email: string | null
}

/** A visit a partner (ESX, 7card...) pays for, from GET /external-visits. */
interface ExternalVisitRow {
  checked_in_at: string
  provider: string
  plan_name: string
  member_email: string | null
  member_name: string
  amount: number
}

/** Excel caps a sheet name at 31 characters and refuses a few symbols. */
function sheetName(name: string, taken: Set<string>): string {
  const base =
    name
      .replace(/[[\]:*?/\\]/g, ' ')
      .trim()
      .slice(0, 31) || 'Partner'
  let candidate = base
  for (let i = 2; taken.has(candidate.toLowerCase()); i++) {
    const suffix = ` ${i}`
    candidate = base.slice(0, 31 - suffix.length) + suffix
  }
  taken.add(candidate.toLowerCase())
  return candidate
}

interface FieldDef {
  key: string
  labelKey: string
}

const FIELD_DEFS: FieldDef[] = [
  { key: 'date', labelKey: 'statistics.revenueExportFieldDate' },
  { key: 'time', labelKey: 'statistics.revenueExportFieldTime' },
  { key: 'email', labelKey: 'statistics.revenueExportFieldEmail' },
  { key: 'plan', labelKey: 'statistics.revenueExportFieldPlan' },
  { key: 'payment', labelKey: 'statistics.revenueExportFieldPayment' },
  { key: 'amount', labelKey: 'statistics.revenueExportFieldAmount' },
]

function isoWeekMonday(year: number, week: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const weekDay = jan4.getUTCDay() || 7
  const monday = new Date(jan4)
  monday.setUTCDate(jan4.getUTCDate() - weekDay + 1 + (week - 1) * 7)
  return monday
}

function periodParamsToRange(params: PeriodParams): { from: string; to: string } {
  const now = new Date()

  if (params.mode === 'fixed' && params.unit === 'month' && params.a) {
    const m = /^(\d{4})-(\d{2})$/.exec(params.a)
    if (m) {
      const year = parseInt(m[1])
      const month = parseInt(m[2])
      const from = new Date(Date.UTC(year, month - 1, 1))
      const to = new Date(Date.UTC(year, month, 1))
      return { from: from.toISOString(), to: to.toISOString() }
    }
  }

  if (params.mode === 'fixed' && params.unit === 'week' && params.a) {
    const m = /^(\d{4})-W(\d{2})$/.exec(params.a)
    if (m) {
      const year = parseInt(m[1])
      const week = parseInt(m[2])
      const monday = isoWeekMonday(year, week)
      const sunday = new Date(monday)
      sunday.setUTCDate(monday.getUTCDate() + 7)
      return { from: monday.toISOString(), to: sunday.toISOString() }
    }
  }

  if (params.mode === 'relative') {
    const period = params.period ?? 'week'
    if (period === 'day') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
      return { from: start.toISOString(), to: now.toISOString() }
    }
    if (period === 'month') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      return { from: start.toISOString(), to: now.toISOString() }
    }
    if (period === 'year') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1))
      return { from: start.toISOString(), to: now.toISOString() }
    }
    // default: week (rolling 7 days)
    const start = new Date(now.getTime() - 7 * 86400000)
    return { from: start.toISOString(), to: now.toISOString() }
  }

  const start = new Date(now.getTime() - 7 * 86400000)
  return { from: start.toISOString(), to: now.toISOString() }
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function RevenueExportModal({ onClose, periodParams, currency, periodLabel }: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [fields, setFields] = useState<Record<string, boolean>>(
    Object.fromEntries(FIELD_DEFS.map((f) => [f.key, true])),
  )
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function toggleField(key: string) {
    setFields((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function buildRows(orders: OrderRow[]): Record<string, string | number>[] {
    return orders.map((o) => {
      const row: Record<string, string | number> = {}
      const d = new Date(o.created_at)
      if (fields.date) row[t('statistics.revenueExportFieldDate')] = d.toLocaleDateString()
      if (fields.time) row[t('statistics.revenueExportFieldTime')] = d.toLocaleTimeString()
      if (fields.email) row[t('statistics.revenueExportFieldEmail')] = o.member_email ?? ''
      if (fields.plan) row[t('statistics.revenueExportFieldPlan')] = o.plan_name
      if (fields.payment) row[t('statistics.revenueExportFieldPayment')] = o.payment_method
      if (fields.amount)
        row[t('statistics.revenueExportFieldAmount', { currency })] = Number(o.total_amount)
      return row
    })
  }

  /**
   * One partner's visits, laid out like the revenue sheet so the two read the
   * same. The payment column names who pays: the partner, not the member.
   */
  function buildVisitRows(visits: ExternalVisitRow[]): Record<string, string | number>[] {
    return visits.map((v) => {
      const row: Record<string, string | number> = {}
      const d = new Date(v.checked_in_at)
      if (fields.date) row[t('statistics.revenueExportFieldDate')] = d.toLocaleDateString()
      if (fields.time) row[t('statistics.revenueExportFieldTime')] = d.toLocaleTimeString()
      if (fields.email) row[t('statistics.revenueExportFieldEmail')] = v.member_email ?? ''
      if (fields.plan) row[t('statistics.revenueExportFieldPlan')] = v.plan_name
      if (fields.payment) row[t('statistics.revenueExportFieldPayment')] = v.provider
      if (fields.amount) row[t('statistics.revenueExportFieldAmount', { currency })] = v.amount
      return row
    })
  }

  /** Partner visits for the period. Empty, not an error, on an older Worker. */
  async function fetchVisits(token: string, from: string, to: string) {
    const res = await fetch(
      `${apiUrl}/external-visits?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (res.status === 404) return []
    if (!res.ok) throw new Error(t('statistics.revenueExportError'))
    return ((await res.json()) as { visits: ExternalVisitRow[] }).visits ?? []
  }

  async function handleExport() {
    setExporting(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const { from, to } = periodParamsToRange(periodParams)
      // The list endpoint pages at 20 by default; an export wants the whole period.
      const [res, visits] = await Promise.all([
        fetch(
          `${apiUrl}/invoices?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=10000`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        fetchVisits(token, from, to),
      ])
      if (!res.ok) throw new Error(t('statistics.revenueExportError'))
      const { invoices: orders } = (await res.json()) as { invoices: OrderRow[] }

      const amountKey = t('statistics.revenueExportFieldAmount', { currency })
      const wb = XLSX.utils.book_new()
      const taken = new Set<string>()

      // Desk and online sales: the same sheet as before partners existed.
      const rows = buildRows(orders)
      const ordersTotal = orders.reduce((sum, o) => sum + Number(o.total_amount), 0)
      if (rows.length > 0) {
        if (fields.amount) {
          rows.push({})
          rows.push({ [amountKey]: ordersTotal })
        }
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(rows),
          sheetName('Revenue', taken),
        )
      }

      // One sheet per partner (ESX, 7card...), each with its own total.
      const byProvider = new Map<string, ExternalVisitRow[]>()
      for (const v of visits) {
        const list = byProvider.get(v.provider) ?? []
        list.push(v)
        byProvider.set(v.provider, list)
      }
      const providerTotals: { provider: string; total: number }[] = []
      for (const [provider, list] of byProvider) {
        const total = list.reduce((sum, v) => sum + Number(v.amount), 0)
        providerTotals.push({ provider, total })
        const sheetRows = buildVisitRows(list)
        if (fields.amount) {
          sheetRows.push({})
          sheetRows.push({ [amountKey]: total })
        }
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(sheetRows),
          sheetName(provider, taken),
        )
      }

      // With partners in the period, a last sheet puts every source side by side.
      if (providerTotals.length > 0) {
        const sourceKey = t('statistics.revenueExportSource')
        const summary: Record<string, string | number>[] = [
          { [sourceKey]: t('statistics.revenueExportOrdersRow'), [amountKey]: ordersTotal },
          ...providerTotals.map((p) => ({ [sourceKey]: p.provider, [amountKey]: p.total })),
          {},
          {
            [sourceKey]: t('statistics.revenueExportTotalRow'),
            [amountKey]: ordersTotal + providerTotals.reduce((sum, p) => sum + p.total, 0),
          },
        ]
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(summary),
          sheetName(t('statistics.revenueExportSummarySheet'), taken),
        )
      }

      if (wb.SheetNames.length > 0) {
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer
        const blob = new Blob([buf], { type: 'application/octet-stream' })
        const ts = new Date().toISOString().slice(0, 10)
        triggerBlobDownload(blob, `revenue-${ts}.xlsx`)
      }
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('statistics.revenueExportError'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <GymDialog onClose={onClose}>
      <div className="p-6">
        <h2 className="mb-5 text-base font-semibold">{t('statistics.revenueExportTitle')}</h2>

        <div className="mb-5">
          <p className="text-sm text-foreground/60">
            {t('statistics.revenueExportPeriod', { period: periodLabel })}
          </p>
        </div>

        <div className="mb-6">
          <p className="mb-2 text-sm font-medium">{t('exports.fieldsLabel')}</p>
          <div className="grid grid-cols-2 gap-2">
            {FIELD_DEFS.map(({ key, labelKey }) => (
              <Checkbox
                key={key}
                isSelected={fields[key]}
                onValueChange={() => toggleField(key)}
                size="sm"
              >
                {labelKey === 'statistics.revenueExportFieldAmount'
                  ? t(labelKey, { currency })
                  : t(labelKey)}
              </Checkbox>
            ))}
          </div>
        </div>

        {error && <p className="mb-4 text-sm text-danger">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="flat" onClick={onClose} isDisabled={exporting}>
            {t('common.cancel')}
          </Button>
          <Button color="primary" onClick={handleExport} isDisabled={exporting}>
            {exporting ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                {t('statistics.revenueExporting')}
              </span>
            ) : (
              t('statistics.revenueExportAction')
            )}
          </Button>
        </div>
      </div>
    </GymDialog>
  )
}
